import { useTranslation } from "react-i18next";
import { IAPIResponse, StatusCode } from "api/protocols";
import { UserActive, UserLogout } from "api/rpc";
import { GetClient } from "api/rpc/2024-04/customer/client";
import { GetClientGuest } from "api/rpc/2024-04/guest/client";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { showError } from "redux/actions/ui";
import { sortFacilities } from "redux/reducers/clientPortal";
import { ClientPortalNavigation } from "../ClientPortalNavigation";
import "./clientPortalReservations.scss";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { GetFacilityGuest } from "api/rpc/2024-04/guest/facility";
import { useClientPortal } from "../useClientPortal";
import { useHistory, useParams } from "react-router-dom";
import { GetFacility } from "api/rpc/2024-04/customer/facility";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import CustomDropdown from "components/customDropdown/CustomDropdown";
import { IFacility } from "redux/reducers/models/facility";
import CustomDropdownOption from "components/customDropdown/CustomDropdownOption";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import Spin from "components/spin/spin";
import Input from "components/form/input/Input";
import axios, { CancelToken } from "axios";
import {
  GetGuestReservationLocation,
  GetGuestReservationModuleAll,
  GetGuestReservationSegment,
} from "api/rpc/2024-04/guest/reservation";
import { ILocation, IModule, ISegment } from "redux/reducers/models/reservations";
import classNames from "classnames";
import LoaderCard from "components/loaderCard/LoaderCard";
import Callout from "components/callout/Callout";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import DateBar from "elements/teesheet/DateBar";
import moment from "moment";
import { GetCustomerReservationSegment } from "api/rpc/2024-04/customer/reservation";
import { LocaleCurrency } from "helpers/Locale";
import { capitalize, formatDate } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import {
  GetCustomerReservationNotificationAll,
  GetGuestReservationNotificationAll,
  IReservationNotification,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";

interface IParams {
  clientShortName: string;
  facilityShortName: string;
}

interface IState {
  facilitySearchQuery: string;
  searchedFacilities: Array<IFacility>;
  modules: Array<IModule>;
  selectedModule: IModule;
  moduleQuery: string;
  segments: { [key: string]: Array<ISegment> };
  locations: Array<ILocation>;
  notifications: IReservationNotification[];
}

export default function ClientPortalReservations() {
  const { clientPortalStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { clientShortName, facilityShortName } = useParams<IParams>();
  const { getURLParams } = useClientPortal();
  const { date, waitlist_reference_id } = getURLParams();
  const [adminAccountPopup, setAdminAccountPopup] = useState<boolean>(false);
  const windowSize = useWindowSize();
  const history = useHistory();
  const [selectedDate, setSelectedDate] = useState<Date>(
    date ? new Date(date.concat(`T${moment().format("hh:mm")}`)) : new Date(),
  );
  const [state, setState] = useState<IState>({
    facilitySearchQuery: "",
    searchedFacilities: [],
    modules: undefined,
    selectedModule: null,
    moduleQuery: "",
    segments: undefined,
    locations: null,
    notifications: [],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(
      clientPortalUpdate({
        reservation: null,
      }),
    );
    if (!clientPortalStore?.user || !clientPortalStore?.client || !clientPortalStore?.selectedFacility) {
      void getClient();
    }
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (clientPortalStore?.selectedFacility) {
      void loadModules(source.token);
    }
    return () => source.cancel();
  }, [clientPortalStore?.selectedFacility]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (state.selectedModule) {
      void loadSegments(source.token, true);
    }

    return () => source.cancel();
  }, [state.selectedModule]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (state.selectedModule) {
      void loadSegments(source.token, false);
    }

    return () => source.cancel();
  }, [selectedDate]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (selectedDate && state.selectedModule) {
      void loadReservationNotifications(selectedDate, source.token);
    }
    return () => source.cancel();
  }, [selectedDate, state.selectedModule]);

  const facilities = useMemo(() => {
    if (state?.facilitySearchQuery) {
      return clientPortalStore?.client?.facilities?.filter(
        facility =>
          facility?.long_name.toLowerCase().includes(state?.facilitySearchQuery?.toLowerCase()) ||
          facility?.city.toLowerCase().includes(state?.facilitySearchQuery?.toLowerCase()),
      );
    } else if (clientPortalStore?.client?.facilities) {
      return clientPortalStore?.client?.facilities;
    } else {
      return [];
    }
  }, [state?.searchedFacilities, clientPortalStore?.client?.facilities, state?.facilitySearchQuery]);

  async function getClient() {
    let currentActiveUser = !!clientPortalStore?.user;
    let selectedClient = clientPortalStore?.client;
    let selectedFacility = clientPortalStore?.selectedFacility;

    const activeUserRes = await UserActive(false);
    if (activeUserRes?.status === StatusCode.OK) {
      currentActiveUser = true;
    }

    if (activeUserRes?.data?.user_type === 2 || activeUserRes?.data?.user_type === 3) {
      setAdminAccountPopup(true);
      return;
    }

    if (
      !clientPortalStore?.client ||
      !clientPortalStore?.selectedFacility ||
      (clientPortalStore?.selectedFacility?.favourite === undefined && currentActiveUser)
    ) {
      let clientRes;
      let facilityRes;
      if (currentActiveUser) {
        currentActiveUser = true;
        clientRes = await GetClient({ short_name: clientShortName }, false);
      } else {
        clientRes = await GetClientGuest({ short_name: clientShortName }, false);
      }
      if (clientRes.status !== StatusCode.OK || clientRes?.data?.length === 0) {
        dispatch(showError("Error loading client"));
        return;
      }
      selectedClient = clientRes?.data[0];
      if (currentActiveUser) {
        facilityRes = await GetFacility({ client_id: selectedClient?.id, order_by: "long_name" }, false);
      } else {
        facilityRes = await GetFacilityGuest({ client_id: selectedClient?.id, order_by: "long_name" }, false);
      }

      if (facilityRes?.status !== StatusCode.OK || facilityRes?.data?.length === 0) {
        dispatch(showError("Error loading facilities"));
        return;
      }

      //Sort by Favourite facilities
      const sortedFacilities = sortFacilities(facilityRes?.data);

      selectedFacility = selectedFacility
        ? sortedFacilities?.find(facility => facility?.id === selectedFacility?.id)
        : facilityShortName
        ? sortedFacilities?.find(facility => facility?.short_name === facilityShortName)
        : sortedFacilities[0];
      if (!selectedFacility && sortedFacilities?.length > 0) {
        selectedFacility = sortedFacilities?.find(facility => !!facility?.module_reservations) ?? sortedFacilities[0];
      }
      selectedClient = { ...selectedClient, facilities: sortedFacilities };
      dispatch(
        clientPortalUpdate({
          client: selectedClient,
          selectedFacility,
          waitlist_reference_id: waitlist_reference_id ?? null,
        }),
      );
    }

    dispatch(
      clientPortalUpdate({
        user: currentActiveUser ? activeUserRes?.data : undefined,
      }),
    );
  }

  function handleFacilityChange(id: number) {
    const foundFacility = clientPortalStore?.client?.facilities?.find(facility => facility?.id === id);
    if (foundFacility) {
      ReactDOM.unstable_batchedUpdates(() => {
        dispatch(clientPortalUpdate({ selectedFacility: foundFacility }));
      });
    }
  }

  async function logoutUser() {
    const logoutRes = await UserLogout(true);

    if (logoutRes.status !== StatusCode.OK) {
      dispatch(showError("Error logging out"));
    }

    window.location.reload();
  }

  async function loadModules(cancelToken: CancelToken) {
    //Display loader
    setState(prevState => ({
      ...prevState,
      modules: undefined,
      selectedModule: null,
      segments: undefined,
      locations: null,
    }));

    const moduleRes = await GetGuestReservationModuleAll(
      { facility_short_name: clientPortalStore?.selectedFacility?.short_name },
      false,
      cancelToken,
    );

    if (moduleRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError("Error getting modules"));
      setState(prevState => ({ ...prevState, modules: [] }));
      return;
    }

    const modules = moduleRes?.data;
    setState(prevState => ({
      ...prevState,
      modules: [...modules],
      selectedModule: modules?.length > 0 ? modules[0] : null,
      segments: modules?.length > 0 ? prevState?.segments : null,
    }));
  }

  async function loadSegments(cancelToken: CancelToken, loadLocations: boolean) {
    //Display loader
    setState(prevState => ({ ...prevState, segments: undefined }));
    let segmentRes;
    if (clientPortalStore?.user) {
      segmentRes = await GetCustomerReservationSegment(
        { module_id: state?.selectedModule?.id, date: moment(selectedDate).format("YYYY-MM-DD") },
        false,
        cancelToken,
      );
      if (segmentRes.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        setState(prevState => ({ ...prevState, segments: null }));
        dispatch(showError("Reservations unavailable"));
        return;
      }
    } else {
      segmentRes = await GetGuestReservationSegment(
        { module_id: state?.selectedModule?.id, date: moment(selectedDate).format("YYYY-MM-DD") },
        false,
        cancelToken,
      );
      if (segmentRes.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        setState(prevState => ({ ...prevState, segments: null }));
        dispatch(showError("Reservations unavailable"));
        return;
      }
    }

    let locations = state?.locations ? [...state?.locations] : [];
    if (loadLocations) {
      const locationRes = await GetGuestReservationLocation(
        { facility_id: clientPortalStore?.selectedFacility?.id, reservation_module_id: state?.selectedModule?.id },
        false,
        cancelToken,
      );
      if (locationRes.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError("Error getting locations"));
        setState(prevState => ({ ...prevState, locations: [] }));
        return;
      }
      locations = locationRes?.data;
    }

    const formattedSegments = segmentRes.data?.reduce(
      (formattedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );
    setState(prevState => ({ ...prevState, segments: formattedSegments, locations: locations }));
  }

  async function loadReservationNotifications(date: Date, token?: CancelToken) {
    // teeSheetToast.show([]);

    const formattedDate: string = formatDate(date);

    const params = {
      date: formattedDate,
      module_id: state?.selectedModule?.id,
      facility_id: state?.selectedModule?.facility_id,
    };

    let notificationRes: IAPIResponse<any>;

    if (clientPortalStore.user) {
      notificationRes = await GetCustomerReservationNotificationAll(params, token ? false : true, token);
    } else {
      notificationRes = await GetGuestReservationNotificationAll(params, token ? false : true, token);
    }

    if (token && token.reason) {
      return;
    }

    if (notificationRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tee sheet notifications"))); // TODO: Translation
      return;
    }

    setState(prevState => ({ ...prevState, notifications: notificationRes.data }));
  }

  function removeNotification(notificationId: number) {
    const notifications = [...state.notifications];
    const index = state.notifications.findIndex(notification => notification.id === notificationId);
    notifications.splice(index, 1);

    setState(prevState => ({ ...prevState, notifications: notifications }));
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleSelectedDateChange(selectedDate: Date) {
    setSelectedDate(selectedDate);
  }

  function handleSelectSegment(location: ILocation, segment: ISegment) {
    dispatch(
      clientPortalUpdate({
        reservation: {
          location: location,
          segment: segment,
          module: state?.selectedModule,
          booking: null,
          bookedSegments: null,
          endTime: null,
        },
      }),
    );
    if (clientPortalStore?.user) {
      history.push(
        `/portal/${clientShortName}/reservations/${
          facilityShortName ?? clientPortalStore?.selectedFacility?.short_name
        }/booking?module_id=${location?.reservation_module_id}&start_time=${segment?.start_time}&date=${
          segment?.date
        }&location_id=${location?.id}`,
      );
    } else {
      history.push(`/portal/${clientShortName}/login`);
    }
  }

  const selectedFacilityElement = (
    <div className="client-portal-reservations-facility-container">
      {clientPortalStore?.client?.facilities?.length > 1 && clientPortalStore?.selectedFacility?.logo_source && (
        <div>
          <img
            className="client-portal-reservations-home-course-logo"
            src={clientPortalStore?.selectedFacility?.logo_source}
          />
        </div>
      )}
      <div>
        <div className="client-portal-reservations-home-course-name">
          {clientPortalStore?.selectedFacility?.long_name}
        </div>
        {clientPortalStore?.selectedFacility && (
          <div className="client-portal-reservations-course-secondary-information">
            {`${clientPortalStore?.selectedFacility?.address_line_1}, ${clientPortalStore?.selectedFacility?.city}, ${clientPortalStore?.selectedFacility?.province_name}, ${clientPortalStore?.selectedFacility?.postal}`}
          </div>
        )}
        {clientPortalStore?.selectedFacility?.address_line_2 && (
          <div className="client-portal-reservations-course-secondary-information">
            {clientPortalStore?.selectedFacility?.address_line_2}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="client-portal-reservations">
      <ClientPortalNavigation showNavOptions />
      {state?.modules?.length > 1 && (
        <div className="client-portal-reservations-tabs-container">
          <div className="client-portal-reservations-tabs-inner-container">
            <div className="client-portal-reservations-container">
              {!state?.modules ? (
                [...Array(4)]?.map((card, cardIndex) => (
                  <LoaderCard key={cardIndex} customCSSProperties={{ width: "100px", height: "40px" }} />
                ))
              ) : state?.modules?.length > 0 ? (
                state?.modules
                  ?.filter(filteredModule =>
                    filteredModule?.title?.toLowerCase()?.includes(state?.moduleQuery?.toLowerCase()),
                  )
                  ?.map((module, index) => {
                    return (
                      <div
                        key={index}
                        className={classNames("client-portal-reservations-tab", {
                          "client-portal-reservations-tab-selected": module?.id === state?.selectedModule?.id,
                        })}
                        onClick={() => setState(prevState => ({ ...prevState, selectedModule: module }))}
                      >
                        {module?.title}
                      </div>
                    );
                  })
              ) : (
                <Callout title="No Reservations Modules Found for this Facility " type="info" />
              )}
            </div>
            <Input
              onChange={handleInputChange}
              value={state?.moduleQuery}
              id="moduleQuery"
              labelHidden
              onSearchClear={() => setState(prevState => ({ ...prevState, moduleQuery: "" }))}
              placeholder="Search"
            />
          </div>
        </div>
      )}

      <div className="client-portal-reservations-options-container">
        {!clientPortalStore?.client?.facilities ? (
          <LoaderCard
            image
            textRows={2}
            customCSSProperties={{ margin: "30px 0", width: windowSize.width <= MOBILE_WIDTH ? "100%" : "400px" }}
          />
        ) : clientPortalStore?.client?.facilities?.length > 1 ? (
          <CustomDropdown
            parentElement={selectedFacilityElement}
            onChange={handleFacilityChange}
            defaultValue={clientPortalStore?.selectedFacility?.id}
            onSearch={searchString => setState(prevState => ({ ...prevState, facilitySearchQuery: searchString }))}
            searchPlacerHolder={t("Search courses...")}
          >
            {facilities ? (
              facilities
                ?.filter(filteredFacility => !!filteredFacility?.module_reservations)
                ?.map((facility, index) => {
                  return (
                    <CustomDropdownOption value={facility.id} key={index}>
                      <div className="client-portal-reservations-facility-option-container">
                        <img
                          className="client-portal-reservations-facility-option-facility-logo"
                          src={facility?.logo_source}
                        />
                        <p className="client-portal-reservations-facility-option-facility-name">
                          {facility?.long_name}
                        </p>
                        <p className="client-portal-reservations-facility-option-facility-address">{facility?.city}</p>
                        {facility?.favourite && (
                          <FontAwesomeIcon
                            size="1x"
                            fixedWidth
                            className="client-portal-reservations-facility-option-favourite"
                            icon={faStar}
                          />
                        )}
                      </div>
                    </CustomDropdownOption>
                  );
                })
            ) : (
              <div className="facility-spinner">
                <Spin />
              </div>
            )}
          </CustomDropdown>
        ) : (
          selectedFacilityElement
        )}
      </div>

      <div className="client-portal-reservations-date-container">
        <div className="client-portal-reservations-date-inner">
          <div className="client-portal-reservations-date-selector">
            <DatePickerInput
              months={1}
              position="left"
              startingDate={selectedDate}
              setStartingDate={handleSelectedDateChange}
              disablePastDays
            />
          </div>
          <div className="client-portal-reservations-date-bar">
            <DateBar numberOfDays={8} selectedDate={selectedDate} onClick={handleSelectedDateChange} />
          </div>
        </div>
      </div>

      {state.notifications &&
        state.notifications.map((notification: any, index: any) => {
          return (
            <div key={index} style={{ margin: "10px 10%" }}>
              <Callout
                onClose={() => removeNotification(notification.id)}
                type="warning"
                title={notification.subject}
                content={notification.content}
              />
            </div>
          );
        })}

      {state?.segments === undefined ? (
        [...Array(12)]?.map((num, index) => (
          <div key={index} className="client-portal-reservations-segments-loader">
            {[...Array(4)]?.map((card, cardIndex) => (
              <LoaderCard key={cardIndex} customCSSProperties={{ width: "100%", height: "40px" }} />
            ))}
          </div>
        ))
      ) : state?.segments && Object.keys(state.segments)?.length > 0 && state?.locations?.length > 0 ? (
        <>
          <h2 className="client-portal-reservations-module-title">{state?.selectedModule?.title}</h2>
          <div className="client-portal-reservations-segments-container">
            <table className="ui-table ui-table-condensed ui-table-separated-horizontal client-portal-reservations-segments-table">
              <thead>
                <tr>
                  <td>Time</td>
                  {state?.locations?.map((location, locationIndex) => {
                    return <td key={locationIndex}>{location.title}</td>;
                  })}
                </tr>
              </thead>
              <tbody>
                {Object.keys(state?.segments)?.map((segmentKey, segmentKeyIndex) => {
                  return (
                    <tr key={segmentKeyIndex}>
                      <td className="text-bold">{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</td>
                      {state?.locations?.map((location, locationIndex) => {
                        const segment = state?.segments[segmentKey]?.find(
                          searchSegment => searchSegment?.location_id === location?.id,
                        );
                        if (segment?.league_id !== null || segment?.tournament_id !== null) {
                          return (
                            <td
                              key={locationIndex}
                              className={classNames({
                                "segment-league": segment?.league_id !== null,
                                "segment-tournament": segment?.tournament_id !== null,
                              })}
                            >
                              <div className="segment-container">
                                {segment?.league_id && (
                                  <>
                                    <p className="text-semibold">
                                      <span>{location.title}, </span>
                                      <span>{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</span>
                                    </p>
                                    <p>
                                      <span>League</span>
                                    </p>
                                  </>
                                )}
                                {segment?.tournament_id && (
                                  <>
                                    <p className="text-semibold">
                                      <span>{location.title}, </span>
                                      <span>{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</span>
                                    </p>
                                    <p>
                                      <span>Tournament</span>
                                    </p>
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        } else if (segment?.blocked_type === "open" && segment?.booking_id === null) {
                          return (
                            <td
                              key={locationIndex}
                              className={classNames("segment-available")}
                              onClick={() => handleSelectSegment(location, segment)}
                            >
                              <div className="segment-container">
                                <p className="text-semibold">
                                  <span>{location.title}, </span>
                                  <span>{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</span>
                                </p>
                                <p>
                                  {state.selectedModule?.display_online_pricing ? (
                                    segment?.blocks[0]?.variant_price ? (
                                      <>
                                        <LocaleCurrency amount={segment?.blocks[0]?.variant_price} /> / hour
                                      </>
                                    ) : null
                                  ) : null}
                                </p>
                              </div>
                            </td>
                          );
                        } else if (segment?.blocked_type === "open_reserved") {
                          return (
                            <td key={locationIndex} className="segment-available">
                              <div className="segment-container">
                                <p>{capitalize(segment?.blocked_type)}</p>
                                {segment?.blocks[0]?.variant_price && (
                                  <LocaleCurrency amount={segment?.blocks[0]?.variant_price} />
                                )}
                              </div>
                            </td>
                          );
                        } else if (segment?.blocked_type === "blocked" || segment?.blocked_type === "booked") {
                          return (
                            <td key={locationIndex} className="segment-disabled">
                              <div className="segment-container">
                                <p>{capitalize(segment?.blocked_type)}</p>
                              </div>
                            </td>
                          );
                        } else {
                          return <td key={locationIndex} className="segment-unavailable"></td>;
                        }
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="client-portal-reservations-segments-loader" style={{ display: "block" }}>
          <Callout type="error" title="No Reservations" content="There are no reservations for this day" />
        </div>
      )}

      <Portal isMounted={adminAccountPopup}>
        <Popup
          open={adminAccountPopup}
          type="warning"
          title="Admin Account Detected"
          description="Looks like you are logged into an admin account. Click logout below to view the tee times"
          onOk={logoutUser}
          okText="Logout"
        />
      </Portal>
    </div>
  );
}
